import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { ColDef } from 'ag-grid-community'
import { AgGridReact } from 'ag-grid-react'
import { useState } from 'react'
import { Link, useParams } from 'react-router'

import { api, can, isActive, templateMethods, type License, type LicenseInput, type SourceDetail, type SourcePatch } from '../api'
import { ErrorBox, FindingsList, Pill, RunStatus } from '../components'
import { dt, duration, elapsed, num, statusKind, yesNo } from '../format'
import { gridTheme } from '../grid'
import { pollWhile, useNow } from '../hooks'
import { SnapshotsTable, TransfersTable } from './zoneTables'
import { HistoryLink } from './history'

const licenseCols: ColDef<License>[] = [
  { field: 'version', headerName: 'v', width: 64 },
  { field: 'spdx_id', headerName: 'SPDX', width: 120, valueFormatter: (p) => p.value ?? '–' },
  { field: 'attribution_text', headerName: 'Attribution', flex: 2, minWidth: 260, tooltipField: 'attribution_text' },
  { field: 'checked_at', headerName: 'Checked', width: 110 },
  { field: 'checked_by', headerName: 'By', width: 120 },
  { field: 'terms_sha256', headerName: 'Terms hash', width: 130, valueFormatter: (p) => (p.value ? p.value.slice(0, 12) : '–'), cellClass: 'mono' },
  { field: 'created_by', headerName: 'Saved by', width: 130 },
  { field: 'created_at', headerName: 'Saved', width: 150, valueFormatter: (p) => dt(p.value) },
  { field: 'notes', flex: 1, minWidth: 160, tooltipField: 'notes' },
]

export function SourceDetailPage() {
  const { slug = '' } = useParams()
  const qc = useQueryClient()
  const me = useQuery({ queryKey: ['me'], queryFn: api.me })
  const now = useNow(1000)
  const q = useQuery({
    queryKey: ['source', slug],
    queryFn: () => api.source(slug),
    // Live run status: poll fast while a run is queued or running.
    refetchInterval: (query) => pollWhile(!!query.state.data?.runs.some(isActive)),
  })
  const audit = useQuery({ queryKey: ['audit', slug], queryFn: () => api.audit({ kind: 'source', slug, limit: 20 }) })
  const [editing, setEditing] = useState(false)

  const onSaved = (d: SourceDetail) => {
    qc.setQueryData(['source', slug], d)
    qc.invalidateQueries({ queryKey: ['sources'] })
    qc.invalidateQueries({ queryKey: ['audit', slug] })
  }
  const snapshot = useMutation({ mutationFn: () => api.snapshotTerms(slug), onSuccess: onSaved })
  const patch = useMutation({ mutationFn: (p: SourcePatch) => api.patchSource(slug, p), onSuccess: onSaved })
  const trigger = useMutation({
    mutationFn: () => api.triggerRun(slug),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['source', slug] })
      qc.invalidateQueries({ queryKey: ['sources'] })
      qc.invalidateQueries({ queryKey: ['audit', slug] })
    },
  })

  if (q.error) return <p className="callout err">{q.error.message}</p>
  if (!q.data) return <p className="muted">Loading…</p>
  const s = q.data
  const lic = s.licenses[0]
  const editor = can(me.data, 'editor')
  const active = s.runs.find(isActive)

  return (
    <section className="page">
      <p className="crumbs">
        <Link to="/">Sources</Link> / <span className="mono">{s.slug}</span>
      </p>
      <header className="page-h">
        <div>
          <h1>{s.name}</h1>
          <p className="sub">
            {s.publisher} · <a href={s.url} target="_blank" rel="noreferrer">{s.url}</a>
          </p>
        </div>
        <div className="row">
          <HistoryLink kind="source" slug={s.slug} />
          {editor && (
            <button
              className="btn btn-primary"
              disabled={trigger.isPending || !!active || s.status === 'disabled'}
              title={active ? `Run #${active.id} is ${active.status}` : 'Queue an ingest run now'}
              onClick={() => trigger.mutate()}
            >
              {active ? 'Running…' : 'Run now'}
            </button>
          )}
          <Pill kind={statusKind(s.status)}>{s.status}</Pill>
          {editor && (
            <select
              aria-label="Status"
              value={s.status}
              disabled={patch.isPending}
              onChange={(e) => patch.mutate({ status: e.target.value })}
            >
              {['draft', 'active', 'paused', 'disabled'].map((v) => (
                <option key={v}>{v}</option>
              ))}
            </select>
          )}
        </div>
      </header>
      {patch.error && <ErrorBox error={patch.error} />}
      {trigger.error && <ErrorBox error={trigger.error} />}
      {active && (
        <div className="progress" role="status">
          <span className="dot" aria-hidden />
          <span>
            <Link to={`/runs/${active.id}`}>Run #{active.id}</Link> <RunStatus run={active} link={false} />
          </span>
          <span className="muted">{active.status === 'running' ? elapsed(active.started_at, null, now) : 'waiting for the worker'}</span>
        </div>
      )}

      <div className="cols-2">
        <div className="card">
          <h2>Source</h2>
          <dl className="kv">
            <dt>Usage mode</dt>
            <dd className="mono">{s.usage_mode}</dd>
            <dt>Access</dt>
            <dd className="mono">{s.access_method}</dd>
            <dt>Schedule</dt>
            <dd className="mono">{s.schedule ?? '–'} <span className="muted">(Europe/Stockholm)</span></dd>
            <dt>Min interval</dt>
            <dd>{s.min_interval_seconds === null ? '–' : duration(s.min_interval_seconds)}</dd>
            <dt>Next run</dt>
            <dd>{s.next_run ? dt(s.next_run) : <span className="muted">not scheduled ({s.status})</span>}</dd>
            <dt>Config</dt>
            <dd><code className="block">{JSON.stringify(s.config)}</code></dd>
          </dl>
          {templateMethods.includes(s.access_method) && (
            <p><Link className="btn btn-sm" to={`/extraction/${s.slug}`}>Files and extraction templates</Link></p>
          )}
          {s.description && <p className="note">{s.description}</p>}
        </div>

        <div className="card">
          <div className="row-between">
            <h2>Licence {lic && <span className="muted mono">v{lic.version}</span>}</h2>
            {editor && (
              <div className="row">
                <button className="btn" disabled={snapshot.isPending || !lic?.terms_url} onClick={() => snapshot.mutate()}>
                  {snapshot.isPending ? 'Fetching…' : 'Snapshot terms'}
                </button>
                <button className="btn btn-primary" onClick={() => setEditing((v) => !v)}>
                  {editing ? 'Cancel' : 'New version'}
                </button>
              </div>
            )}
          </div>
          {snapshot.error && <ErrorBox error={snapshot.error} />}
          {lic ? (
            <>
              <p className="lic-name">
                {lic.name} {lic.spdx_id && <code>{lic.spdx_id}</code>}
              </p>
              {lic.attribution_required ? (
                <figure className="attribution">
                  <figcaption>Attribution text (exact, used on every page and API response)</figcaption>
                  <blockquote>{lic.attribution_text || <span className="muted">empty</span>}</blockquote>
                  {lic.attribution_text && (
                    <button className="btn btn-sm" onClick={() => navigator.clipboard.writeText(lic.attribution_text ?? '')}>
                      Copy
                    </button>
                  )}
                </figure>
              ) : (
                <p className="callout warn">
                  Published without attribution or licence mention (decided in v{lic.version} by {lic.checked_by}). This source is left out of
                  API responses, headers, the registry and OpenAPI.
                </p>
              )}
              <dl className="kv">
                <dt>Commercial use</dt>
                <dd>{yesNo(lic.commercial_use)}</dd>
                <dt>Redistribution</dt>
                <dd>{yesNo(lic.redistribution)}</dd>
                <dt>Share-alike</dt>
                <dd>{yesNo(lic.share_alike)}</dd>
                <dt>Caching limit</dt>
                <dd>{duration(lic.caching_limit_seconds)}</dd>
                <dt>Terms</dt>
                <dd>
                  {lic.terms_url ? <a href={lic.terms_url} target="_blank" rel="noreferrer">{lic.terms_url}</a> : '–'}
                  {lic.terms_sha256 && <span className="muted mono"> · sha256 {lic.terms_sha256.slice(0, 16)}…</span>}
                </dd>
                <dt>Checked</dt>
                <dd>{lic.checked_at ?? '–'} by {lic.checked_by ?? '–'}</dd>
              </dl>
            </>
          ) : (
            <p className="muted">No licence recorded yet.</p>
          )}
          <FindingsList findings={s.license_findings} empty="Licence record is complete." />
        </div>
      </div>

      {editing && lic !== undefined && (
        <LicenseForm
          initial={lic}
          slug={slug}
          checker={me.data?.name ?? ''}
          onDone={(d) => {
            onSaved(d)
            setEditing(false)
          }}
        />
      )}
      {editing && lic === undefined && (
        <LicenseForm slug={slug} checker={me.data?.name ?? ''} onDone={(d) => { onSaved(d); setEditing(false) }} />
      )}

      <div className="card">
        <h2>Licence history</h2>
        <p className="note">Versions are never edited; a change is always a new version.</p>
        <div className="grid-wrap" style={{ height: 56 + Math.max(1, s.licenses.length) * 42 }}>
          <AgGridReact<License> theme={gridTheme} rowData={s.licenses} columnDefs={licenseCols} getRowId={(p) => String(p.data.version)} tooltipShowDelay={300} />
        </div>
      </div>

      <div className="cols-2">
        <div className="card">
          <div className="row-between">
            <h2>Runs</h2>
            <Link to="/runs" className="note">All runs</Link>
          </div>
          {s.runs.length === 0 ? (
            <p className="muted">No runs yet.{s.status !== 'active' && ' Set the source to active to schedule it, or use Run now.'}</p>
          ) : (
            <table className="tbl">
              <thead><tr><th>#</th><th>Status</th><th>Trigger</th><th>Started</th><th>Duration</th><th className="num">Loaded</th></tr></thead>
              <tbody>
                {s.runs.map((r) => (
                  <tr key={r.id}>
                    <td><Link to={`/runs/${r.id}`} className="mono">#{r.id}</Link></td>
                    <td><RunStatus run={r} />{r.error && <div className="err-t note">{r.error}</div>}</td>
                    <td>{r.trigger}</td>
                    <td>{dt(r.started_at ?? r.created_at)}</td>
                    <td>{elapsed(r.started_at, r.finished_at, now)}</td>
                    <td className="num">{num(r.rows_out)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <div className="card">
          <h2>Change log</h2>
          <ul className="audit">
            {audit.data?.map((a) => (
              <li key={a.id}>
                <span className="mono muted">{dt(a.at)}</span> <strong>{a.actor_name}</strong> {a.action}
                {a.version !== null && <span className="mono"> v{a.version}</span>}
              </li>
            ))}
          </ul>
        </div>
      </div>

      {s.access_method === 'axfr' && (
        <>
          <div className="card">
            <h2>Zone snapshots</h2>
            {s.snapshots.length === 0 ? <p className="muted">No zone transferred yet.</p> : <SnapshotsTable rows={s.snapshots} />}
          </div>
          <div className="card">
            <h2>Zone checks</h2>
            <p className="note">One row per check. The SOA serial is checked first; the zone is only transferred when it changed, at most once per min interval.</p>
            {s.transfers.length === 0 ? <p className="muted">No checks yet.</p> : <TransfersTable rows={s.transfers} />}
          </div>
        </>
      )}
    </section>
  )
}


type Tri = 'yes' | 'no' | 'unknown'
const tri = (v: boolean | null | undefined): Tri => (v === true ? 'yes' : v === false ? 'no' : 'unknown')
const untri = (v: Tri): boolean | null => (v === 'yes' ? true : v === 'no' ? false : null)

function LicenseForm({ initial, slug, checker, onDone }: { initial?: License; slug: string; checker: string; onDone: (d: SourceDetail) => void }) {
  const [f, setF] = useState(() => ({
    name: initial?.name ?? '',
    spdx_id: initial?.spdx_id ?? '',
    commercial_use: tri(initial?.commercial_use),
    redistribution: tri(initial?.redistribution),
    share_alike: initial?.share_alike ?? false,
    caching_limit_seconds: initial?.caching_limit_seconds?.toString() ?? '',
    attribution_text: initial?.attribution_text ?? '',
    attribution_required: initial?.attribution_required ?? true,
    terms_url: initial?.terms_url ?? '',
    checked_at: new Date().toISOString().slice(0, 10),
    checked_by: checker,
    notes: '',
  }))
  const save = useMutation({
    mutationFn: () => {
      const body: LicenseInput = {
        name: f.name,
        spdx_id: f.spdx_id || null,
        commercial_use: untri(f.commercial_use),
        redistribution: untri(f.redistribution),
        share_alike: f.share_alike,
        caching_limit_seconds: f.caching_limit_seconds === '' ? null : Number(f.caching_limit_seconds),
        attribution_text: f.attribution_text,
        attribution_required: f.attribution_required,
        terms_url: f.terms_url || null,
        checked_at: f.checked_at || null,
        checked_by: f.checked_by,
        notes: f.notes,
      }
      return api.addLicense(slug, body)
    },
    onSuccess: onDone,
  })
  const set = <K extends keyof typeof f>(k: K) => (v: (typeof f)[K]) => setF((x) => ({ ...x, [k]: v }))

  return (
    <form
      className="card form"
      onSubmit={(e) => {
        e.preventDefault()
        save.mutate()
      }}
    >
      <h2>New licence version {initial && <span className="muted mono">v{initial.version + 1}</span>}</h2>
      <div className="form-grid">
        <label>Licence name<input value={f.name} onChange={(e) => set('name')(e.target.value)} required /></label>
        <label>SPDX id<input value={f.spdx_id} onChange={(e) => set('spdx_id')(e.target.value)} placeholder="CC-BY-4.0" /></label>
        <label>Commercial use<TriSelect value={f.commercial_use} onChange={set('commercial_use')} /></label>
        <label>Redistribution<TriSelect value={f.redistribution} onChange={set('redistribution')} /></label>
        <label className="check"><input type="checkbox" checked={f.share_alike} onChange={(e) => set('share_alike')(e.target.checked)} />Share-alike</label>
        <label>Caching limit (seconds, empty = none)<input inputMode="numeric" pattern="[0-9]*" value={f.caching_limit_seconds} onChange={(e) => set('caching_limit_seconds')(e.target.value)} /></label>
        <label className="check wide">
          <input type="checkbox" checked={!f.attribution_required} onChange={(e) => set('attribution_required')(!e.target.checked)} />
          Publish without attribution or licence mention (the source is left out of API responses; commercial use and redistribution must still be decided)
        </label>
        {f.attribution_required && (
          <label className="wide">Attribution text (exact wording)<textarea rows={3} value={f.attribution_text} onChange={(e) => set('attribution_text')(e.target.value)} /></label>
        )}
        <label className="wide">Terms URL<input type="url" value={f.terms_url} onChange={(e) => set('terms_url')(e.target.value)} /></label>
        <label>Checked on<input type="date" value={f.checked_at} onChange={(e) => set('checked_at')(e.target.value)} /></label>
        <label>Checked by<input value={f.checked_by} onChange={(e) => set('checked_by')(e.target.value)} /></label>
        <label className="wide">Notes (what changed and why)<textarea rows={2} value={f.notes} onChange={(e) => set('notes')(e.target.value)} /></label>
      </div>
      {save.error && <ErrorBox error={save.error} />}
      <div className="row">
        <button className="btn btn-primary" type="submit" disabled={save.isPending}>
          {save.isPending ? 'Saving…' : 'Save as new version'}
        </button>
        <span className="note">Changing the terms URL clears the terms snapshot; take a new one after saving.</span>
      </div>
    </form>
  )
}

function TriSelect({ value, onChange }: { value: Tri; onChange: (v: Tri) => void }) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value as Tri)}>
      <option value="unknown">not decided</option>
      <option value="yes">yes</option>
      <option value="no">no</option>
    </select>
  )
}
